import React, { useCallback, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ModalDialog } from '../../common/ModalDialog';
import { useVerificationCode } from './hooks';
import styles from './VerifyYourAccountModal.module.css';
import FigoLottie from '../../common/FigoLottie';

const STEPS = {
  ENTER_CODE: 1,
  SEND_CODE: 0,
};

const FooterButtons = ({
  cancelLabel = '',
  loadingProceed = false,
  onCancel = () => { },
  onProceed = () => { },
  proceedButtonDisabled = false,
  proceedLabel = '',
}) => (
  <div className={styles.actions}>
    <button
      className={styles.cancelButton}
      onClick={onCancel}
      type="button"
    >
      {cancelLabel}
    </button>

    <button
      className={styles.sendCodeButton}
      disabled={proceedButtonDisabled}
      onClick={onProceed}
      type="button"
    >
      {loadingProceed ? <FigoLottie height={15} width={15} /> : proceedLabel}
    </button>
  </div>
);

const VerifyYourAccountModal = ({
  contactMethod = '',
  errorMessage = '',
  loading = false,
  maskedContact = '',
  onClose = () => { },
  onSendCode = () => { },
  onVerifyCode = () => { },
  remainingSeconds = 0,
  visible = false,
}) => {
  const { t } = useTranslation('insurance');
  const [stepNumber, setStepNumber] = useState(STEPS.SEND_CODE);
  const {
    code,
    handleChange,
    handleKeyDown,
    handlePaste,
    inputRefs,
    isComplete,
    reset,
    inputValues,
  } = useVerificationCode();

  const handleOnSendCode = useCallback(() => {
    setStepNumber(STEPS.ENTER_CODE);
    onSendCode();
  }, [onSendCode]);

  const handleOnClose = useCallback(() => {
    setStepNumber(STEPS.SEND_CODE);
    reset();
    onClose();
  }, [onClose, reset]);

  const handleOnVerifyCode = useCallback(() => {
    onVerifyCode(code);
  }, [code]);

  const onInputChange = useCallback((index) => (event) => {
    handleChange(index, event);
  }, [handleChange]);

  const onInputKeyDown = useCallback((index) => (event) => {
    handleKeyDown(index, event);
  }, [handleKeyDown]);

  const renderContent = useCallback(() => {
    switch (stepNumber) {
      case STEPS.SEND_CODE:
        return (
          <div className={styles.contentContainer}>
            <p className={styles.description}>
              {t('verifyAccountModal.description')}
            </p>

            <p className={styles.footer}>
              <Trans
                components={{ bold: <strong /> }}
                i18nKey="verifyAccountModal.footer"
                t={t}
                values={{ contactMethod, maskedContact }}
              />
            </p>

            <FooterButtons
              cancelLabel={t('verifyAccountModal.cancel')}
              onCancel={handleOnClose}
              onProceed={handleOnSendCode}
              proceedLabel={t('verifyAccountModal.sendCode')}
            />
          </div>
        );

      case STEPS.ENTER_CODE:
        return (
          <div className={styles.contentContainer}>
            <h2 className={styles.enterCodeBelow}>
              {t('verifyAccountModal.enterCodeBelow')}
            </h2>

            <div className={styles.codeInputsContainer}>
              {inputValues.map((value, index) => (
                <input
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  ref={(input) => { inputRefs.current[index] = input; }}
                  className={styles.codeInput}
                  inputMode="numeric"
                  maxLength={1}
                  onChange={onInputChange(index)}
                  onKeyDown={onInputKeyDown(index)}
                  onPaste={handlePaste}
                  type="text"
                  value={value}
                />
              ))}
            </div>

            <button
              className={styles.resendPasscodeButton}
              disabled={remainingSeconds > 0}
              onClick={onSendCode}
              type="button"
            >
              {remainingSeconds > 0
                ? t('verifyAccountModal.resendPasscodeCooldown', {
                  remainingSeconds,
                })
                : t('verifyAccountModal.resendPasscode')}
            </button>

            {errorMessage
              && (<span className={styles.errorMessage}>{errorMessage}</span>)}

            <FooterButtons
              cancelLabel={t('verifyAccountModal.cancel')}
              loadingProceed={loading}
              onCancel={handleOnClose}
              onProceed={handleOnVerifyCode}
              proceedButtonDisabled={!isComplete}
              proceedLabel={t('verifyAccountModal.verify')}
            />
          </div>
        );

      default:
        return null;
    }
  }, [
    contactMethod,
    errorMessage,
    handleChange,
    handleKeyDown,
    handleOnClose,
    handleOnSendCode,
    handleOnVerifyCode,
    handlePaste,
    inputRefs,
    inputValues,
    isComplete,
    loading,
    maskedContact,
    remainingSeconds,
    stepNumber,
    t,
  ]);

  return (
    <ModalDialog onClose={handleOnClose} show={visible}>
      <div className={styles.content}>
        <h2 className={styles.title}>{t('verifyAccountModal.title')}</h2>

        {renderContent()}
      </div>
    </ModalDialog>
  );
};

export { VerifyYourAccountModal };
